import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  PersonPin as ActiveUserIcon,
  Book as BookIcon,
  Group as GroupIcon,
  Autorenew as ReprocessIcon,
} from '@mui/icons-material';
import { differenceInDays } from 'date-fns';
import { useAnalyticsDashboard } from '../hooks/useAnalyticsDashboard';
import KPICard from '../components/analytics/KPICard';
import DateRangePicker from '../components/analytics/DateRangePicker';
import DonutChart from '../components/analytics/DonutChart';
import HistoryLineChart from '../components/analytics/HistoryLineChart';
import FunnelChart from '../components/analytics/FunnelChart';
import DeliveriesBarChart from '../components/analytics/DeliveriesBarChart';
import { useAuth } from '../contexts/AuthContext';

const AnalyticsDashboardPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('analytics', 'editar');
  const {
    data,
    loading,
    error,
    startDate,
    endDate,
    setDateRange,
    setLast30Days,
    reprocessing,
    reprocessError,
    reprocessResult,
    reprocessPeriod,
  } = useAnalyticsDashboard();

  const handleReprocess = () => {
    const periodDays = differenceInDays(endDate, startDate) + 1;
    if (periodDays > REPROCESS_CONFIRM_THRESHOLD_DAYS) {
      const ok = window.confirm(
        `O período selecionado tem ${periodDays} dias. Reprocessar períodos acima de ` +
          `${REPROCESS_CONFIRM_THRESHOLD_DAYS} dias pode levar mais tempo e gerar um alto volume ` +
          `de leitura/escrita no banco de dados.\n\nDeseja continuar?`
      );
      if (!ok) return;
    }
    reprocessPeriod();
  };

  const isEmpty =
    !loading &&
    !error &&
    data !== null &&
    data.activeUsersInPeriod === 0 &&
    data.totalTitlesDelivered === 0 &&
    data.usersWhoReceivedTitles === 0;

  // Adapta dados de histórico para o formato esperado pelo HistoryLineChart
  const titlesHistory = (data?.titlesDeliveredHistory ?? []).map((item) => ({
    period: item.period,
    value: item.totalTitlesDelivered,
  }));

  const usersHistory = (data?.usersReceivedTitlesHistory ?? []).map((item) => ({
    period: item.period,
    value: item.usersWhoReceivedTitles,
  }));

  const companiesData = (data?.deliveriesByCompany ?? []).map((item) => ({
    id: item.companyId,
    name: item.companyName,
    value: item.totalTitlesDelivered,
  }));

  const titlesData = (data?.deliveriesByTitle ?? []).map((item) => ({
    id: item.titleId,
    name: item.titleName,
    value: item.totalTitlesDelivered,
  }));

  const productsData = (data?.deliveriesByProduct ?? []).map((item) => ({
    id: item.productId,
    name: item.productName,
    value: item.totalTitlesDelivered,
  }));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
          Dashboard Analítica
        </Typography>
        <DateRangePicker
          startDate={startDate}
          endDate={endDate}
          onDateChange={setDateRange}
          loading={loading}
          actions={
            <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Analytics.'}>
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={
                    reprocessing ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <ReprocessIcon fontSize="small" />
                    )
                  }
                  disabled={!canManage || reprocessing || loading}
                  onClick={handleReprocess}
                >
                  Reprocessar dados do período
                </Button>
              </span>
            </Tooltip>
          }
        />
        {reprocessError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {reprocessError}
          </Alert>
        )}
        {reprocessResult && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Reprocessamento concluído: {reprocessResult.eventsProcessed} evento(s) em{' '}
            {reprocessResult.daysProcessed} dia(s) ({(reprocessResult.durationMs / 1000).toFixed(1)}s).
          </Alert>
        )}
      </Box>

      {/* Erro */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={setLast30Days}>
              Tentar novamente
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Empty state (período sem dados) */}
      {isEmpty && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Nenhum dado disponível para o período selecionado
        </Alert>
      )}

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <KPICard
            title="Usuários cadastrados"
            value={data?.totalRegisteredUsers ?? null}
            loading={loading}
            icon={<PeopleIcon fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <KPICard
            title="Empresas cadastradas"
            value={data?.totalCompanies ?? null}
            loading={loading}
            icon={<BusinessIcon fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <KPICard
            title="Usuários ativos no período"
            value={data?.activeUsersInPeriod ?? null}
            loading={loading}
            subtitle="no período selecionado"
            icon={<ActiveUserIcon fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <KPICard
            title="Títulos entregues no período"
            value={data?.totalTitlesDelivered ?? null}
            loading={loading}
            subtitle="no período selecionado"
            icon={<BookIcon fontSize="small" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2.4}>
          <KPICard
            title="Usuários contemplados no período"
            value={data?.usersWhoReceivedTitles ?? null}
            loading={loading}
            subtitle="no período selecionado"
            icon={<GroupIcon fontSize="small" />}
          />
        </Grid>
      </Grid>

      {/* Donut + Funil */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={5}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Usuários ativos vs. não ativos
              </Typography>
              <DonutChart
                active={data?.donut.active ?? 0}
                inactive={data?.donut.inactive ?? 0}
                loading={loading}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Funil de engajamento
              </Typography>
              <FunnelChart
                titlesReceived={data?.funnel.titlesReceived ?? 0}
                readingStarted={data?.funnel.readingStarted ?? 0}
                readingCompleted={data?.funnel.readingCompleted ?? 0}
                loading={loading}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Históricos */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Títulos entregues por mês (últimos 6 meses)
              </Typography>
              <HistoryLineChart
                data={titlesHistory}
                label="Títulos entregues"
                loading={loading}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Usuários contemplados por mês (últimos 6 meses)
              </Typography>
              <HistoryLineChart
                data={usersHistory}
                label="Usuários contemplados"
                loading={loading}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Distribuições */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} lg={4}>
          <Card variant="outlined">
            <CardContent>
              <DeliveriesBarChart
                data={companiesData}
                title="Entregas por empresa"
                loading={loading}
                granularityNote={GRANULARITY_NOTE}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <Card variant="outlined">
            <CardContent>
              <DeliveriesBarChart
                data={titlesData}
                title="Entregas por título"
                loading={loading}
                granularityNote={GRANULARITY_NOTE}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <Card variant="outlined">
            <CardContent>
              <DeliveriesBarChart
                data={productsData}
                title="Entregas por produto"
                loading={loading}
                granularityNote={GRANULARITY_NOTE}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const GRANULARITY_NOTE = 'Os dados refletem os meses completos abrangidos pelo período selecionado';

/** Acima deste limiar, o reprocessamento pede confirmação (alto volume de leitura/escrita). */
const REPROCESS_CONFIRM_THRESHOLD_DAYS = 31;

export default AnalyticsDashboardPage;
